// Public paywall design tokens. This file is the single registry for the
// publisher CSS-var escape hatch and the Appearance/template/mode theme layer.
//
// Resolution (styles.css :host):
//
//   --sm-TOKEN: var(--sm-TOKEN-theme, <classic default>)
//
//   public --sm-TOKEN — publishers override this on #ezoic-sm from page CSS
//                       (id beats :host, so it wins over the theme layer).
//   -theme            — Appearance accent (theme.ts) and Modern/Dark in styles.css.
//   default           — classic/light built-in when the theme layer is unset.
//
// Adding a token: append here, add the :host declaration + any Modern/Dark
// -theme values in styles.css, extend docs/architecture/paywall-theming.md,
// and keep tokens.contract.test.ts green.

export const PUBLIC_TOKENS = [
  'font-family',
  'text',
  'text-secondary',
  'text-muted',
  'surface',
  'surface-hover',
  'bg',
  'border',
  'border-strong',
  'accent',
  'accent-hover',
  'on-accent',
  'focus',
  'danger',
  'success-bg',
  'success-fg',
  'danger-bg',
  'radius-sm',
  'radius',
  'radius-lg',
  'overlay-bg',
  'shadow',
  'shadow-lg',
] as const;

export type PublicToken = (typeof PUBLIC_TOKENS)[number];

// Accent tokens Appearance writes on the -theme layer (derived from one hex).
export const APPEARANCE_ACCENT_TOKENS = ['accent', 'accent-hover', 'focus', 'on-accent'] as const satisfies readonly PublicToken[];

export function publicVar(token: PublicToken): string {
  return `--sm-${token}`;
}

export function themeVar(token: PublicToken): string {
  return `--sm-${token}-theme`;
}

// Classic/light defaults — must match the innermost fallbacks in styles.css :host.
export const CLASSIC_LIGHT_DEFAULTS: Record<PublicToken, string> = {
  'font-family': "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  text: '#0f172a',
  'text-secondary': '#64748b',
  'text-muted': '#6b7a90',
  surface: '#ffffff',
  'surface-hover': '#f1f5f9',
  bg: '#f8fafc',
  border: '#e2e8f0',
  'border-strong': '#cbd5e1',
  accent: '#0a2540',
  'accent-hover': '#12385c',
  'on-accent': '#ffffff',
  focus: 'rgba(10, 37, 64, 0.14)',
  danger: '#b42318',
  'success-bg': '#ecfdf3',
  'success-fg': '#067647',
  'danger-bg': '#fef3f2',
  'radius-sm': '6px',
  radius: '8px',
  'radius-lg': '14px',
  'overlay-bg': 'rgba(15, 23, 42, 0.58)',
  shadow: '0 24px 64px rgba(15, 23, 42, 0.24), 0 2px 10px rgba(15, 23, 42, 0.08)',
  'shadow-lg': '0 36px 90px rgba(15, 23, 42, 0.3), 0 8px 24px rgba(15, 23, 42, 0.12)',
};
